import type { AxisAssessment } from "@shared/labs/level-assessment";
import type { SignalStrength } from "@shared/labs/level-assessment-axis-signals";
import { labelForSignal } from "@shared/labs/level-assessment-signal-labels";
import { ChevronRight } from "lucide-react";
import { useState } from "react";
import { StrengthBars } from "./critical-signals";
import { C, UI } from "./ui-tokens";

const STRENGTH_TEXT: Record<SignalStrength, string> = {
  strong: "Forte",
  moderate: "Moderado",
  weak: "Fraco",
};
const STRENGTH_ORDER: SignalStrength[] = ["strong", "moderate", "weak"];

function formatSignalValue(value: number | boolean | string | null): string {
  // `null` = the extractor explicitly abstained (unknown) — routed but not
  // observed; render as absence, never as evidence.
  if (value === null) return "Não observado";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "number") return Number.isInteger(value) ? String(value) : value.toFixed(2);
  return String(value);
}

/**
 * The full set of signals that fed an axis score, collapsed by default and
 * grouped by strength tier (Forte/Moderado/Fraco). The complete, honest audit
 * surface behind the decisive-signal cards — every routed signal with its value
 * and strength.
 */
export function AllSignals({ signals }: { signals: NonNullable<AxisAssessment["signalsUsed"]> }) {
  const [open, setOpen] = useState(false);
  const groups = STRENGTH_ORDER.map((strength) => ({
    strength,
    rows: signals.filter((s) => s.strength === strength).sort((a, b) => a.key.localeCompare(b.key)),
  })).filter((g) => g.rows.length > 0);

  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        data-testid="all-signals-toggle"
        style={{
          ...UI,
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "12px 14px",
          borderRadius: 11,
          border: `1px solid ${C.border}`,
          background: C.paper,
          fontSize: 13,
          fontWeight: 600,
          color: C.ink,
          cursor: "pointer",
        }}
      >
        <span>Todos os sinais ({signals.length})</span>
        <ChevronRight
          size={15}
          color={C.ink50}
          style={{ transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}
        />
      </button>

      {open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 16 }}>
          {groups.map((g) => (
            <div key={g.strength}>
              <div
                style={{
                  ...UI,
                  fontSize: 11,
                  fontWeight: 700,
                  color: C.ink50,
                  marginBottom: 7,
                }}
              >
                {STRENGTH_TEXT[g.strength]} · {g.rows.length}
              </div>
              <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, overflow: "hidden" }}>
                {g.rows.map((s, i) => (
                  <div
                    key={s.key}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr auto 96px",
                      alignItems: "center",
                      gap: 12,
                      padding: "9px 14px",
                      borderTop: i === 0 ? "none" : `1px solid ${C.borderSoft}`,
                    }}
                  >
                    <span style={{ ...UI, fontSize: 12.5, color: C.ink, fontWeight: 500 }}>
                      {labelForSignal(s.key).label}
                    </span>
                    <StrengthBars strength={s.strength} />
                    <span
                      style={{
                        ...UI,
                        fontSize: 12.5,
                        fontWeight: 700,
                        color:
                          s.value === false || s.value === 0 || s.value === null ? C.ink50 : C.ink,
                        textAlign: "right",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {formatSignalValue(s.value)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
